import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getUserSubscriptions } from '../services/users';
import { isAuthenticated } from '../services/storage';

const UserSubscriptionContext = createContext(null);

/**
 * Provedor global de assinatura do usuário logado.
 * Expõe `subscription`, `subscriptionScheduling` e `refresh()`.
 * Deve envolver toda a aplicação (definido em index.jsx).
 */
export function UserSubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [subscriptionScheduling, setSubscriptionScheduling] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Re-busca os dados de assinatura do usuário na API.
   * Chamar após qualquer mutação de plano ou agendamento.
   * @param {boolean} [silent=false] - Se true, atualiza os dados sem acionar `isLoading`.
   *   Usado no polling de fundo para não disparar o `LoadingPage` do PrivateRoute a cada ciclo
   *   (evita a sensação de "recarregar a página" periodicamente).
   * @returns {Promise<void>}
   */
  const refresh = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const data = await getUserSubscriptions();
      setSubscription(data.subscription ?? null);
      setSubscriptionScheduling(data.subscriptionScheduling ?? null);
    } catch (err) {
      console.error('❌ [UserSubscriptionContext] ~ refresh ~ Erro:', err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // Carrega na montagem e mantém polling de 60s para detectar mudanças de plano em tempo real
  useEffect(() => {
    if (!isAuthenticated()) {
      setIsLoading(false); // sem token → não há dados a buscar; libera o PrivateRoute
      return;
    }
    refresh(); // Carga inicial: aciona isLoading para o PrivateRoute aguardar antes de decidir bloqueio
    const interval = setInterval(() => {
      if (!isAuthenticated()) return; // Parar silenciosamente se usuário deslogou
      refresh(true); // Polling de fundo silencioso: não pisca o LoadingPage a cada 60s
    }, 60_000);
    return () => clearInterval(interval);
  }, [refresh]);

  const value = useMemo(
    () => ({
      subscription,
      subscriptionScheduling,
      setSubscriptionScheduling,
      isLoading,
      refresh,
    }),
    [subscription, subscriptionScheduling, isLoading, refresh]
  );

  return <UserSubscriptionContext.Provider value={value}>{children}</UserSubscriptionContext.Provider>;
}

export function useUserSubscriptionContext() {
  const context = useContext(UserSubscriptionContext);
  if (!context) {
    throw new Error('useUserSubscriptionContext deve ser usado dentro de UserSubscriptionProvider');
  }
  return context;
}
